import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, Search, ChevronDown, ChevronRight, Syringe, Layers, Heart, Ear, Home, AlertCircle, Scan
} from 'lucide-react';
import { categories, procedures } from '@/data/procedures';

const categoryIcons: Record<string, React.ReactNode> = {
  msk: <Syringe className="w-4 h-4" />,
  derm: <Layers className="w-4 h-4" />,
  womens: <Heart className="w-4 h-4" />,
  audiology: <Ear className="w-4 h-4" />,
  pocus: <Scan className="w-4 h-4" />,
};

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['msk', 'derm', 'womens', 'audiology']);
  const [searchResults, setSearchResults] = useState<typeof procedures>([]);
  const [showSearch, setShowSearch] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchQuery.trim().length > 1) {
      const q = searchQuery.toLowerCase();
      const results = procedures.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.overview.description.toLowerCase().includes(q) ||
          p.overview.indications.some((i) => i.toLowerCase().includes(q))
      );
      setSearchResults(results);
      setShowSearch(true);
    } else {
      setSearchResults([]);
      setShowSearch(false);
    }
  }, [searchQuery]);

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const isActive = (path: string) => location.pathname === path;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-border">
        <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5 text-primary-foreground" />
        </div>
        <div>
          <div className="font-semibold text-sm text-foreground leading-tight">FM Procedure Hub</div>
          <div className="text-[11px] text-muted-foreground leading-tight">Primary Health Care Residency</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {/* Home */}
        <Link
          to="/"
          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-colors mb-1 ${
            isActive('/') ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
          }`}
        >
          <Home className="w-4 h-4 flex-shrink-0" />
          Home
        </Link>

        <div className="h-px bg-border my-2 mx-1" />

        {/* Categories */}
        {categories.map((cat) => {
          const procs = procedures.filter((p) => p.categoryId === cat.id);
          const isExpanded = expandedCategories.includes(cat.id);

          return (
            <div key={cat.id} className="mb-1">
              <button
                onClick={() => toggleCategory(cat.id)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {categoryIcons[cat.id]}
                  {cat.label}
                </div>
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>

              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="ml-2 mt-0.5 space-y-0.5">
                      {procs.map((proc) => {
                        const active = isActive(`/procedure/${proc.id}`);
                        return (
                          <Link
                            key={proc.id}
                            to={`/procedure/${proc.id}`}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                              active
                                ? 'bg-primary/12 text-primary font-medium border-l-2 border-primary'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                          >
                            {proc.title}
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-border px-4 py-3">
        <p className="text-[10px] text-muted-foreground leading-snug">
          For educational use only. Not a substitute for clinical judgment.
          <br />Primary Health Care — Des Moines, IA
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 border-r border-border bg-card flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed left-0 top-0 bottom-0 z-50 w-72 bg-card border-r border-border flex flex-col lg:hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-border">
                <span className="font-semibold text-sm">FM Procedure Hub</span>
                <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-md hover:bg-muted">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <SidebarContent />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-border bg-card/80 backdrop-blur-sm z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md hover:bg-muted transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="lg:hidden text-sm font-semibold text-foreground">FM Procedure Hub</span>
          </div>

          {/* Search */}
          <div className="relative w-full max-w-sm mx-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search procedures..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={() => setTimeout(() => setShowSearch(false), 150)}
              onFocus={() => searchQuery.length > 1 && setShowSearch(true)}
              className="w-full pl-9 pr-4 py-1.5 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <AnimatePresence>
              {showSearch && searchResults.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden max-h-72 overflow-y-auto"
                >
                  {searchResults.map((proc) => (
                    <button
                      key={proc.id}
                      onMouseDown={() => {
                        navigate(`/procedure/${proc.id}`);
                        setSearchQuery('');
                        setShowSearch(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-0"
                    >
                      <div className="text-sm font-medium text-foreground">{proc.title}</div>
                      <div className="text-xs text-muted-foreground">{proc.category}</div>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="hidden sm:inline text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md font-medium">
              Point-of-Care Reference
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
